/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
  ColumnHeightOutlined,
  GiftOutlined,
  UserOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { convertToImperial } from '@sportninja/common/reducers/helpers'
import React from 'react'
import { font, paragraphSmall } from 'src/components/css'
import GenderIcon from 'src/components/icons/GenderIcon'
import SkillLevelIcon from 'src/components/icons/SkillLevelIcon'

export interface PlayerInfoRowProps {
  age?: number | null
  weight?: number | null
  /** Height in METRES, as the player profile returns it. Converted to
   * feet/inches for display via the shared helper (same as mobile). */
  height?: string | number | null
  skillLevel?: string | null
  skillColor?: string
  gender?: string | null
}

/**
 * Header stats as icon chips, split across two rows so the physical metrics
 * read as one group and the descriptive attributes as another:
 *
 *   row 1 — age · weight · height
 *   row 2 — gender · skill level
 *
 * Each chip renders only when its data exists, and a row disappears entirely
 * when none of its chips have data. Icons match the profile header's stats row
 * (`ProfilePlayerStatsRow`): gift for age, person for weight, column-height for
 * height. Skill uses the shared growth-ramp glyph tinted by its own colour.
 */
const PlayerInfoRow: React.FC<PlayerInfoRowProps> = ({
  age,
  weight,
  height,
  skillLevel,
  skillColor,
  gender,
}) => {
  'use memo'
  const [ft, inch] = convertToImperial(height as never)
  const hasHeight = ft !== '' && ft != null
  const hasMetrics = age != null || weight != null || hasHeight
  const hasAttributes = Boolean(gender) || Boolean(skillLevel)

  if (!hasMetrics && !hasAttributes) {
    return null
  }

  return (
    <div css={stackStyle}>
      {hasMetrics && (
        <div css={rowStyle}>
          {age != null && (
            <span css={chipStyle}>
              <GiftOutlined css={iconStyle} />
              <span css={textStyle}>{age} yo</span>
            </span>
          )}

          {weight != null && (
            <span css={chipStyle}>
              <UserOutlined css={iconStyle} />
              <span css={textStyle}>{weight} lbs</span>
            </span>
          )}

          {hasHeight && (
            <span css={chipStyle}>
              <ColumnHeightOutlined css={iconStyle} />
              <span css={textStyle}>
                {ft}&apos;{inch}&quot;
              </span>
            </span>
          )}
        </div>
      )}

      {hasAttributes && (
        <div css={rowStyle}>
          {!!gender && (
            <span css={chipStyle}>
              <GenderIcon size={14} color={colors.PRIMARY} title='Gender' />
              <span css={textStyle}>{gender}</span>
            </span>
          )}

          {!!skillLevel && (
            <span css={chipStyle}>
              {/* Tinted by the skill's own ramp colour, unlike the PRIMARY
                  chips above — matches mobile. */}
              <SkillLevelIcon
                skillLevel={skillLevel}
                size={14}
                color={skillColor ?? colors.NEUTRAL_50}
              />
              <span
                css={[
                  textStyle,
                  skillColor &&
                    css`
                      color: ${skillColor};
                    `,
                ]}
              >
                {skillLevel}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default PlayerInfoRow

/* Stacks the metrics row above the attributes row. */
const stackStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

/* Keeps each glyph and its value on one line. */
const chipStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
`

const iconStyle = css`
  color: ${colors.PRIMARY};
  font-size: 14px;
`

const textStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.NEUTRAL_50};
`
